import React, { useState, useEffect, useCallback } from "react";
import { CancelRequest } from "../../services/AxiosInstance";
import { Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Block from "../../components/Block/Block";
import { Icon, MediaGroup, Media, MediaText, Image } from "../../components";
import DataTable from "react-data-table-component";
import { getDriverListApi } from "../../services/UserService";
import { toInitials, perPageOptions, userStatusOptions } from "../../utilities";
import { connect } from "react-redux";
import { debounce } from "lodash";
import DebounceSearchInput from "../Constant/DebounceSearchInput";
import { StaticSelectInput } from "../Constant/SelectInput";
import Pagination from "../Constant/Pagination";

function DriverListPage({ userRole }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    page_size: 6,
    query: "",
    status: "",
  });

  const handleSearchChange = debounce((value) => {
    CancelRequest();
    setFilter((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  }, 100);

  const fetchData = useCallback(() => {
    CancelRequest();
    setIsLoading(true);
    const queryParams = {
      page: filter.page,
      page_size: filter.page_size,
      query: filter.query,
      status: filter.status,
    };
    getDriverListApi(queryParams)
      .then((resp) => {
        const allUsers = resp.data.results;
        setData(allUsers);
        setTotal(resp.data.count);
      })
      .catch((error) => {
        console.error(error.response?.data?.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter.query, filter.page, filter.page_size, filter.status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePagination = (page) => {
    setFilter((prev) => ({
      ...prev,
      page: page.selected + 1,
    }));
  };

  const handleSelectChange = (name, value) => {
    console.log(value);
    setFilter((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  const columns = [
    {
      name: "Name",
      grow: 2,
      selector: (row) => row.user.fullname,
      cell: (row) => (
        <MediaGroup>
          <Media size="md" shape="circle" variant="dark-soft">
            {row?.profile_photo ? (
              <Image
                src={`${process.env.REACT_APP_BASE_URL}${row?.profile_photo}`}
                staticImage
                alt="user"
              />
            ) : (
              <span className="fw-medium">
                {toInitials(row?.user.fullname)}
              </span>
            )}
          </Media>

          <MediaText>
            <Link
              to={`/driver/${row?.user.id}`}
              className="title text-uppercase text-primary"
            >
              <strong>{row?.user.fullname}</strong>{" "}
            </Link>
            <Link to={`/driver/${row?.user.id}`}>
              <span className="small text">{row?.user.email}</span>
            </Link>
          </MediaText>
        </MediaGroup>
      ),
      sortable: true,
    },
    {
      name: "Mobile",
      selector: (row) => row?.user.mobile_number,
      cell: (row) => <span> {row?.user.mobile_number}</span>,
      sortable: false,
    },
    {
      name: "DL Number",
      selector: (row) => row?.dl_number,
      cell: (row) => <span> {row?.dl_number}</span>,
      sortable: false,
    },
    {
      name: "Last Login",
      selector: (row) => row?.user.last_login,
      cell: (row) => row?.user.last_login,
      sortable: false,
    },
    {
      name: "Status",
      selector: (row) => row.user.is_active,
      cell: (row) => (
        <span
          className={`badge text-bg-${
            row.user.is_active ? "success-soft" : "danger-soft"
          }`}
        >
          {row?.user.is_active ? "Active" : "Inactive"}
        </span>
      ),
      sortable: false,
    },
  ];

  return (
    <div>
      <div className="d-flex flex-column flex-md-row justify-content-between mb-2">
        <div>
          <span
            className="text-primary"
            style={{ fontSize: "1.3rem", fontWeight: "500" }}
          >
            Drivers
          </span>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Drivers
            </li>
          </ol>
        </div>
        <div className="d-flex flex-column flex-md-row gap-2">
          <div>
            <DebounceSearchInput handleSearchChange={handleSearchChange} />
          </div>
          <div>
            <StaticSelectInput
              setParentInputValue={handleSelectChange}
              optionsList={userStatusOptions}
              name="status"
              id="status"
              parentClassName="mt-0"
              isClearable
              placeholder="Select Status"
            />
          </div>
          {/* <div className="data-table-action-wrap">
                <StaticSelectInput
                  setParentInputValue={handleSelectChange}
                  optionsList={perPageOptions}
                  name="page_size"
                  id="page_size"
                  parentClassName="mt-0"
                  isClearable
                  placeholder="Per Page"
                />
              </div> */}
          <Link
            to={`/add-driver`}
            className="btn btn-primary d-md-inline-flex align-self-start"
          >
            <Icon name="plus" />
            <span>Add Driver</span>
          </Link>
        </div>
      </div>
      <Block>
        <Card>
          {isLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : data?.length === 0 ? (
            <div className="p-2 text-center">There are no records found.</div>
          ) : (
            <div className="table-responsive pb-1">
              <table className="table mb-0 fs-6 align-middle">
                <thead className="custom-thead table-light">
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>DL Number</th>
                    <th>Last Login</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => (
                    <tr key={index}>
                      {/* Name + Avatar + Email */}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-dark text-white d-flex justify-content-center align-items-center overflow-hidden"
                            style={{
                              width: "35px",
                              height: "35px",
                              fontWeight: 500,
                              fontSize: "0.9rem",
                            }}
                          >
                            {row?.profile_photo ? (
                              <img
                                src={`${process.env.REACT_APP_BASE_URL}${row.profile_photo}`}
                                alt="user"
                                className="rounded-circle"
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <span>{toInitials(row.user.fullname)}</span>
                            )}
                          </div>
                          <div>
                            <Link
                              to={`/driver/${row?.user.id}`}
                              className="text-uppercase text-primary fw-bold d-block"
                            >
                              {row?.user.fullname}
                            </Link>
                            <Link
                              to={`/driver/${row?.user.id}`}
                              className="small text-secondary d-block"
                            >
                              {row?.user.email}
                            </Link>
                          </div>
                        </div>
                      </td>

                      {/* Mobile */}
                      <td>{row?.user.mobile_number}</td>

                      {/* DL Number */}
                      <td>{row?.dl_number}</td>

                      {/* Last Login */}
                      <td>{row?.user.last_login}</td>

                      {/* Status */}
                      <td>
                        <span
                          className={`badge ${
                            row.user.is_active
                              ? "bg-success-subtle text-success"
                              : "bg-danger-subtle text-danger"
                          }`}
                        >
                          {row.user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Custom Pagination Component */}
          {!isLoading && data?.length > 0 && (
            <div className="border-top p-4">
              <Pagination
                currentPage={filter.page}
                recordsPage={filter.page_size}
                dataLength={total}
                handlePagination={handlePagination}
              />
            </div>
          )}
        </Card>
      </Block>
    </div>
  );
}
const mapStateToProps = (state) => ({
  userRole: state.auth.user?.role || "user",
});

export default connect(mapStateToProps)(DriverListPage);
